import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import { api, data as dataApi } from '@/utils/auth'
import { printCourtReport } from '@/utils/printUtils'
import Button from '@/components/common/Button'
import {
  IconSparkles, IconSchool, IconSearch, IconMessage2, IconClipboardList,
  IconLock, IconReceipt, IconFileText, IconGavel, IconShield,
  IconBuildingBank, IconUsers, IconCreditCard, IconScale,
} from '@tabler/icons-react'

// ── Module ID → Tabler icon (Tribunal — replaces emoji) ─────────────────────
const MODULE_ICONS = {
  'ask':              IconSparkles,
  'coach':            IconSchool,
  'case-analyst':     IconSearch,
  'comms':            IconMessage2,
  'events':           IconClipboardList,
  'direct-messages':  IconLock,
  'expenses':         IconReceipt,
  'documents':        IconFileText,
  'orders':           IconGavel,
  'cps':              IconShield,
  'attorney-match':   IconBuildingBank,
  'children':         IconUsers,
  'credit':           IconCreditCard,
}

// ── Seen-features tracker (localStorage) ─────────────────────────────────────
const SEEN_KEY = 'cg_seen_features'

function getSeenFeatures() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') } catch { return {} }
}

function markFeatureSeen(id) {
  const seen = getSeenFeatures()
  seen[id] = Date.now()
  localStorage.setItem(SEEN_KEY, JSON.stringify(seen))
}

function useSeenFeatures() {
  const [seen, setSeen] = useState(getSeenFeatures)
  const markSeen = useCallback((id) => {
    markFeatureSeen(id)
    setSeen(getSeenFeatures())
  }, [])
  return [seen, markSeen]
}

// ── Dashboard customization prefs (localStorage) ─────────────────────────────
const DASH_PREFS_KEY = 'cg_dash_prefs'

// IDs shown by default — most-used core features only
export const DASH_DEFAULT_MODULES = new Set([
  'comms', 'events', 'violations',       // evidence core
  'orders', 'documents',                 // legal core
  'parenting-time', 'children',          // parenting core
  'expenses',                            // finance core
  'ask',                                 // AI core
])

// full ordered list for iteration
const ALL_MODULE_IDS = [
  'comms', 'events', 'violations', 'timeline',
  'orders', 'documents', 'cps', 'attorney-match',
  'parenting-time', 'children', 'medical', 'wellbeing',
  'expenses', 'expense-analytics', 'child-support', 'credit',
  'ask', 'coach', 'case-analyst',
]

export function getDashPrefs() {
  try {
    const raw = localStorage.getItem(DASH_PREFS_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  // default: only the core set
  const prefs = {}
  ALL_MODULE_IDS.forEach(id => { prefs[id] = DASH_DEFAULT_MODULES.has(id) })
  return prefs
}

export function saveDashPrefs(prefs) {
  localStorage.setItem(DASH_PREFS_KEY, JSON.stringify(prefs))
}

// ── Location detection ────────────────────────────────────────────────────────
const LOC_KEY          = 'cg_location'
const LOC_ATTEMPT_KEY  = 'cg_loc_attempts'  // visits without a successful location
const LOC_RETRY_EVERY  = 5                   // re-prompt every N unsuccessful visits

function useLocationDetect({ onDetected } = {}) {
  const [loc,        setLoc]        = useState(() => {
    try { return JSON.parse(localStorage.getItem(LOC_KEY) || 'null') } catch { return null }
  })
  const [showPrompt, setShowPrompt] = useState(false)
  const [detecting,  setDetecting]  = useState(false)
  const [locError,   setLocError]   = useState(null)

  useEffect(() => {
    if (loc) return // already have location — nothing to do

    // Count visits without a successful location fix
    const prev     = parseInt(localStorage.getItem(LOC_ATTEMPT_KEY) || '0', 10)
    const attempts = prev + 1
    localStorage.setItem(LOC_ATTEMPT_KEY, String(attempts % LOC_RETRY_EVERY))

    // Show prompt on first visit OR every LOC_RETRY_EVERY visits (attempt resets to 0)
    if (attempts === 1 || attempts % LOC_RETRY_EVERY === 0) {
      const t = setTimeout(() => setShowPrompt(true), 800)
      return () => clearTimeout(t)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function requestGps() {
    setDetecting(true)
    setShowPrompt(false)
    setLocError(null)
    try {
      let lat, lon

      // ── Native iOS: use Capacitor Geolocation plugin ─────────────────────
      if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
        // eslint-disable-next-line import/no-unresolved
        const { Geolocation } = await import(/* @vite-ignore */ '@capacitor/geolocation')
        const perm = await Geolocation.requestPermissions()
        if (perm.location === 'denied') {
          throw Object.assign(new Error('denied'), { code: 1 })
        }
        const pos = await Geolocation.getCurrentPosition({
          timeout:            15000,
          maximumAge:         3_600_000,
          enableHighAccuracy: false,
        })
        lat = pos.coords.latitude
        lon = pos.coords.longitude
      } else {
        // ── Web / Safari: use browser geolocation API ──────────────────────
        if (!navigator.geolocation) {
          setLocError('Location services are not available on this device.')
          setDetecting(false)
          return
        }
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout:            15000,
            maximumAge:         3_600_000,
            enableHighAccuracy: false,
          })
        )
        lat = pos.coords.latitude
        lon = pos.coords.longitude
      }

      // ── Reverse-geocode via Pages Function ───────────────────────────────
      const res  = await fetch(`/api/location?lat=${lat}&lon=${lon}`)
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      const location = {
        state:  data.state  || null,
        county: data.county || null,
        city:   data.city   || null,
        source: 'gps',
      }
      localStorage.setItem(LOC_KEY, JSON.stringify(location))
      localStorage.setItem(LOC_ATTEMPT_KEY, '0')
      setLoc(location)
      onDetected?.(location)
    } catch (err) {
      const denied = err?.code === 1
      setLocError(
        denied
          ? 'Location access was denied. Enable it in Settings → Privacy → Location Services → GoodGuardian.'
          : 'Could not detect location. You can set your state manually in Settings.'
      )
    } finally {
      setDetecting(false)
    }
  }

  function dismiss() {
    setShowPrompt(false)
    // Don't reset counter on dismiss — let it keep counting toward the next retry
  }

  return { loc, showPrompt, detecting, locError, requestGps, dismiss }
}

// ── Module categories ─────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'ai',       label: 'AI Assistants',  icon: '✨', showHeader: true  },
  { id: 'tracking', label: 'Daily Tracking', icon: '📆', showHeader: false },
  { id: 'legal',    label: 'Legal Tools',    icon: '⚖️', showHeader: false },
  { id: 'family',   label: 'Family',         icon: '👨‍👧', showHeader: false },
]

// ── Module cards config ───────────────────────────────────────────────────────
// newSince: ISO date string — show "New" badge until parent visits the feature
const modules = [
  {
    id: 'ask',
    to: '/ask',
    emoji: '✨',
    title: 'Ask Anything',
    desc: 'One question — automatically routed to the right AI specialist.',
    badge: null,
    newSince: '2026-03-24',
    color: 'from-brand-700 via-brand-500 to-violet-500',
    textColor: 'text-white',
    large: true,
    category: 'ai',
  },
  {
    id: 'coach',
    to: '/coach',
    emoji: '🎓',
    title: 'Court Prep Coach',
    desc: 'Plain-language prep for hearings, testimony & more.',
    badge: null,
    color: 'from-violet-500 to-brand-700',
    textColor: 'text-white',
    large: false,
    category: 'ai',
  },
  {
    id: 'case-analyst',
    to: '/case-analyst',
    emoji: '🔍',
    title: 'Case Analyst',
    desc: 'AI assessment of your legal situation.',
    badge: null,
    color: 'from-brand-100 to-blue-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'ai',
  },
  {
    id: 'comms',
    to: '/comms',
    emoji: '💬',
    title: 'Comms Log',
    desc: 'Court-ready co-parenting records.',
    badge: null,
    color: 'from-sky-100 to-brand-100',
    textColor: 'text-slate-800',
    large: false,
    category: 'tracking',
  },
  {
    id: 'events',
    to: '/events',
    emoji: '📋',
    title: 'Event Log',
    desc: 'Log custody events & violations.',
    badge: null,
    color: 'from-indigo-100 to-indigo-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'tracking',
  },
  {
    id: 'direct-messages',
    to: '/direct-messages',
    emoji: '🔒',
    title: 'Secure Messages',
    desc: 'Encrypted co-parent & attorney channels.',
    badge: null,
    color: 'from-emerald-100 to-teal-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'tracking',
  },
  {
    id: 'expenses',
    to: '/expenses',
    emoji: '🧾',
    title: 'Expense Log',
    desc: 'Track & approve shared child expenses.',
    badge: null,
    color: 'from-brand-100 to-sky-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'tracking',
  },
  {
    id: 'documents',
    to: '/documents',
    emoji: '📄',
    title: 'Documents',
    desc: 'Generate & manage legal documents.',
    badge: null,
    color: 'from-indigo-50 to-brand-100',
    textColor: 'text-slate-800',
    large: false,
    category: 'legal',
  },
  {
    id: 'orders',
    to: '/orders',
    emoji: '⚖️',
    title: 'Court Orders',
    desc: 'Track and comply with court orders.',
    badge: null,
    color: 'from-amber-100 to-yellow-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'legal',
  },
  {
    id: 'cps',
    to: '/cps',
    emoji: '🛡️',
    title: 'CPS Tracker',
    desc: 'Track your case, know your rights.',
    badge: null,
    color: 'from-red-100 to-rose-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'legal',
  },
  {
    id: 'attorney-match',
    to: '/attorney-match',
    emoji: '🏛️',
    title: 'Find an Attorney',
    desc: 'Personalized guidance to find the right family law attorney.',
    badge: null,
    newSince: '2026-03-24',
    color: 'from-brand-700 to-brand-500',
    textColor: 'text-white',
    large: false,
    category: 'legal',
  },
  {
    id: 'children',
    to: '/children',
    emoji: '👶',
    title: 'Children',
    desc: 'Profiles, school & medical records.',
    badge: null,
    color: 'from-pink-100 to-rose-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'family',
  },
  {
    id: 'credit',
    to: '/credit',
    emoji: '💳',
    title: 'Credit Score',
    desc: 'Track scores from all 3 bureaus.',
    badge: null,
    newSince: '2026-03-25',
    color: 'from-green-100 to-emerald-50',
    textColor: 'text-slate-800',
    large: false,
    category: 'family',
  },
]

// ── Quick action items (unique actions not covered by bottom nav) ─────────────
const quickActions = [
  { emoji: '📝', label: 'New Document',  to: '/documents'       },
  { emoji: '📊', label: 'Court Report',  to: '__court_report__' },
]

// ── Situation → tip mapping ───────────────────────────────────────────────────
const tips = {
  divorcing: {
    icon: '⚖️',
    text: 'Starting a divorce? Your first step is usually filing a petition in your state. Ask the AI guide for a step-by-step breakdown.',
  },
  custody: {
    icon: '👨‍👧',
    text: 'In a custody dispute? Document everything — exchanges, communications, missed visits. The Event Log is your best friend.',
  },
  cps: {
    icon: '🏛️',
    text: 'Dealing with CPS? You have the right to remain silent and request an attorney. Use the CPS Tracker for step-by-step guidance.',
  },
}

// ── Co-parenting rotating affirmations (one picked fresh each session) ─────────
const COPARENTING_AFFIRMATIONS = [
  { icon: '🌱', text: 'Your children thrive when both parents communicate with respect. Every kind word you choose today shapes their tomorrow.' },
  { icon: '💪', text: 'Co-parenting is hard — and you\'re doing it anyway. That takes real strength and real love for your kids.' },
  { icon: '🌟', text: 'Keeping records isn\'t just protection — it\'s proof of how consistently you show up for your children.' },
  { icon: '🤝', text: 'A business-like tone with your co-parent isn\'t cold — it\'s smart. Keep it brief, calm, and focused on the kids.' },
  { icon: '💛', text: 'Your children don\'t need perfect parents. They need parents who try — and you\'re trying every single day.' },
  { icon: '🎯', text: 'Focus on what you can control: your words, your actions, your consistency. Let the rest go.' },
  { icon: '🌈', text: 'Every smooth custody exchange is a small win. You made that happen — celebrate it.' },
  { icon: '📋', text: 'Logging your parenting time isn\'t paranoia — it\'s preparation. Future you will be grateful.' },
  { icon: '🧘', text: 'You can\'t control your co-parent\'s behavior, only your response to it. Breathe first, reply second.' },
  { icon: '🏅', text: 'Showing up reliably and lovingly is the most powerful thing you can do for your child\'s future.' },
  { icon: '✉️', text: 'When in doubt, keep messages short and factual. A clear, calm sentence beats a paragraph every time.' },
  { icon: '🌻', text: 'Healing and co-parenting at the same time is one of the hardest things a person can do. Be gentle with yourself.' },
  { icon: '🔒', text: 'Your Communication Log is your quiet advocate — it records the truth so you don\'t have to argue it later.' },
  { icon: '🌍', text: 'Your child is watching how you handle hard things. Let them see resilience, patience, and grace.' },
  { icon: '⭐', text: 'Every time you put your child\'s needs above the conflict, you\'re giving them a gift they\'ll carry for life.' },
  { icon: '🎗️', text: 'You don\'t have to like your co-parent to co-parent well. Courtesy and consistency are enough.' },
  { icon: '🛡️', text: 'Staying organized — events, communications, exchanges — is your best defense and your best advocate.' },
  { icon: '💬', text: 'Respond, don\'t react. A ten-minute pause before replying to a difficult message can save you days of stress.' },
  { icon: '🌙', text: 'On the hard days, remember: your children knowing they are deeply loved by you is more than enough.' },
  { icon: '🎉', text: 'Co-parenting wins are quiet ones — a smooth handoff, a shared decision, a kind exchange. Notice and celebrate yours.' },
]

// ── Event type colors ─────────────────────────────────────────────────────────
const EVENT_COLORS = {
  'custody-exchange': 'bg-blue-100 text-blue-700',
  appointment:        'bg-purple-100 text-purple-700',
  school:             'bg-green-100 text-green-700',
  court:              'bg-amber-100 text-amber-700',
  compliance:         'bg-red-100 text-red-700',
  other:              'bg-slate-100 text-slate-600',
}

const EVENT_TYPES = {
  'custody-exchange': { label: '🔄 Custody Exchange',  badge: 'bg-blue-100 text-blue-700'    },
  appointment:        { label: '🏥 Appointment',        badge: 'bg-purple-100 text-purple-700' },
  school:             { label: '🎓 School Event',       badge: 'bg-green-100 text-green-700'  },
  court:              { label: '⚖️ Court Hearing',       badge: 'bg-amber-100 text-amber-700'  },
  compliance:         { label: '📋 Order Compliance',   badge: 'bg-red-100 text-red-700'      },
  other:              { label: '📌 Other',               badge: 'bg-slate-100 text-slate-600'  },
}

function canDeleteEvent(event, userId) {
  return event.created_by === userId || event.ownership === 'own'
}

// ── Event detail bottom sheet (dashboard) ─────────────────────────────────────
function EventDetailModal({ event, onClose, onDelete, userId, children }) {
  if (!event) return null
  const cfg    = EVENT_TYPES[event.event_type || event.type] || EVENT_TYPES.other
  const badge  = cfg.badge.split(' ')
  const canDel = canDeleteEvent(event, userId)
  const child  = children?.find(c => c.id === event.child_id)

  const formatDate = (ymd) => {
    if (!ymd) return ''
    const [y, m, d] = ymd.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    })
  }

  const handleDelete = () => { onDelete(event.id); onClose() }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div
        className="w-full max-w-lg mx-auto bg-white rounded-t-3xl flex flex-col animate-slide-up max-h-[85vh]"
        role="dialog" aria-modal="true" aria-label="Event details"
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex-shrink-0 pt-4 pb-2 flex justify-center">
          <div className="w-12 h-1 rounded-full bg-slate-200" />
        </div>

        {/* Colour strip */}
        <div className={`mx-6 mb-4 rounded-2xl px-4 py-3 flex items-center gap-3 ${badge[0]} border ${badge[0].replace('bg-', 'border-').replace('100', '200')}`}>
          <span className="text-2xl">{cfg.label.split(' ')[0]}</span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-800 text-base leading-tight">{event.title}</p>
            <span className={`text-xs font-semibold ${badge[1]}`}>
              {cfg.label.split(' ').slice(1).join(' ')}
            </span>
          </div>
        </div>

        <div className="overflow-y-auto no-scrollbar px-6 pb-6 flex flex-col gap-4">
          {/* Date */}
          <div className="flex flex-col gap-1">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Date &amp; Time</p>
            <p className="text-sm font-semibold text-slate-800">{formatDate(event.start_date || event.date)}</p>
            {(event.start_time || event.end_time) && (
              <p className="text-xs text-slate-500">
                {event.start_time ? `at ${event.start_time}` : ''}
                {event.end_time  ? ` – ${event.end_time}` : ''}
              </p>
            )}
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Location</p>
              <p className="text-sm text-slate-700 flex items-start gap-1.5">
                <span>📍</span><span>{event.location}</span>
              </p>
            </div>
          )}

          {/* Notes */}
          {event.notes && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Notes</p>
              <p className="text-sm text-slate-700 leading-relaxed">{event.notes}</p>
            </div>
          )}

          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            {event.ownership === 'partner' && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">👥 Co-parent event</span>
            )}
            {event.visibility === 'private' && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">🔒 Private</span>
            )}
            {event.visibility === 'shared' && (
              <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">🔗 Shared</span>
            )}
            {child && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full font-medium">👦 {child.name}</span>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Close</Button>
            {canDel && (
              <Button variant="danger" size="md" className="flex-1" onClick={handleDelete}>Delete Event</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Module card ── Tribunal: flat white tiles, hero = slate ink with gold ──
function ModuleCard({ mod, isNew, onSeen, onClick, unreadCount = 0 }) {
  const isDisabled = !!mod.badge

  function handleClick() {
    if (isDisabled) return
    if (isNew) onSeen(mod.id)
    onClick(mod.to)
  }

  const Icon = MODULE_ICONS[mod.id]
  const isHero = !!mod.large

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={[
        'relative rounded-2xl p-4 text-left transition-all duration-200',
        isHero
          ? 'flex flex-row items-center gap-3 col-span-2 xl:col-span-3 min-h-[112px] bg-brand-700 text-white'
          : 'flex flex-col justify-between gap-2.5 min-h-[112px] bg-white dark:bg-[#1A1F26] border border-slate-200 dark:border-white/10',
        isDisabled ? 'opacity-70 cursor-not-allowed' : 'active:scale-[0.97]',
      ].join(' ')}
    >
      {unreadCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center z-10 animate-pulse">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
      {isDisabled && (
        <span className={`absolute top-2.5 right-2.5 text-xs font-bold px-2 py-0.5 rounded-full ${
          isHero ? 'bg-white/25 text-white' : 'bg-gold-100 text-gold-700'
        }`}>
          {mod.badge}
        </span>
      )}
      {!isDisabled && isNew && !unreadCount && (
        <span className="absolute top-2.5 right-2.5 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-gold-400 text-brand-700 select-none">
          New
        </span>
      )}
      {isHero ? (
        <>
          <div className="w-12 h-12 rounded-xl bg-gold-400 flex items-center justify-center flex-shrink-0">
            {Icon && <Icon size={26} stroke={1.75} className="text-brand-700" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base leading-snug text-white">{mod.title}</p>
            <p className="text-xs mt-0.5 leading-snug text-white/75">{mod.desc}</p>
          </div>
        </>
      ) : (
        <>
          {Icon && <Icon size={22} stroke={1.75} className="text-slate-700 dark:text-gold-300" />}
          <div>
            <p className="font-semibold text-[13.5px] leading-snug text-slate-800 dark:text-slate-100">{mod.title}</p>
            {mod.desc && (
              <p className="text-xs mt-0.5 leading-snug text-slate-600 dark:text-slate-400">
                {mod.desc}
                {unreadCount > 0 && (
                  <span className="ml-1.5 font-semibold text-red-500">{unreadCount} unread</span>
                )}
              </p>
            )}
          </div>
        </>
      )}
    </button>
  )
}

// ── Upcoming events card ──────────────────────────────────────────────────────
function UpcomingEventsCard({ calendarEvents, onNavigate, onEventTap }) {
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const upcoming = useMemo(() => calendarEvents
    .filter(e => {
      const d = new Date(e.start_date || e.date)
      return d >= today
    })
    .sort((a, b) => new Date(a.start_date || a.date) - new Date(b.start_date || b.date))
    .slice(0, 3),
    [calendarEvents, today]
  )

  if (upcoming.length === 0) return null

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="section-kicker mb-0">Upcoming</h2>
        <button onClick={() => onNavigate('/calendar')}
          className="text-sm text-brand-600 font-semibold flex items-center gap-0.5 hover:text-brand-800">
          View all
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 3l5 5-5 5" />
          </svg>
        </button>
      </div>
      <div className="card overflow-hidden divide-y divide-slate-100">
        {upcoming.map(event => {
          const d = new Date(event.start_date || event.date)
          const colorClass = EVENT_COLORS[event.event_type] || EVENT_COLORS.other
          return (
            <button
              key={event.id}
              onClick={() => onEventTap(event)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-50 flex flex-col items-center justify-center flex-shrink-0">
                <p className="text-xs font-bold text-brand-500 uppercase leading-none">
                  {d.toLocaleDateString('en-US', { month: 'short' })}
                </p>
                <p className="text-lg font-extrabold text-brand-700 leading-none">{d.getDate()}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate leading-snug">{event.title}</p>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colorClass}`}>
                  {(event.event_type || 'other').replace('-', ' ')}
                </span>
              </div>
              {event.ownership === 'partner' && (
                <span className="text-xs text-slate-600 font-medium flex-shrink-0">Co-parent</span>
              )}
            </button>
          )
        })}
      </div>
    </section>
  )
}

// ── Credit score widget ───────────────────────────────────────────────────────
function CreditScoreWidget({ onNavigate }) {
  const [latest, setLatest] = useState(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    api('/api/data/credit').then(r => r.json()).then(d => {
      if (d.scores?.length) setLatest(d.scores[0])
    }).catch(() => {}).finally(() => setLoaded(true))
  }, [])

  function scoreColor(s) {
    if (!s) return 'text-slate-400'
    if (s >= 740) return 'text-green-600'
    if (s >= 670) return 'text-lime-600'
    if (s >= 580) return 'text-amber-600'
    return 'text-red-600'
  }
  function scoreLabel(s) {
    if (!s) return null
    if (s >= 800) return 'Exceptional'
    if (s >= 740) return 'Very Good'
    if (s >= 670) return 'Good'
    if (s >= 580) return 'Fair'
    return 'Poor'
  }

  return (
    <button onClick={() => onNavigate('/credit')}
      className="w-full card p-4 text-left active:scale-[0.98] transition-transform">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">💳</span>
          <span className="text-base font-semibold text-slate-700">Credit Score</span>
        </div>
        <span className="text-sm text-brand-500 font-medium">View →</span>
      </div>
      {!loaded ? (
        <p className="text-sm text-slate-600">Loading…</p>
      ) : latest ? (
        <div className="flex items-end gap-3">
          <span className={`text-3xl font-bold ${scoreColor(latest.average)}`}>{latest.average}</span>
          <div className="pb-1">
            <p className={`text-sm font-semibold ${scoreColor(latest.average)}`}>{scoreLabel(latest.average)}</p>
            <p className="text-xs text-slate-600">avg across {[latest.equifax, latest.experian, latest.transunion].filter(Boolean).length} bureau{[latest.equifax, latest.experian, latest.transunion].filter(Boolean).length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-600">Tap to add your credit scores</p>
      )}
    </button>
  )
}

// ── Co-parent status card ─────────────────────────────────────────────────────
function CoParentStatusCard({ coParents = [], onNavigate }) {
  const hasLinked = coParents.length > 0

  if (hasLinked) {
    return (
      <div className="flex flex-col gap-2">
        <button
          onClick={() => onNavigate('/coparent')}
          className="w-full card p-4 text-left active:scale-[0.98] transition-transform"
        >
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2 flex-shrink-0">
              {coParents.slice(0, 3).map((cp, i) => (
                <div
                  key={cp.id}
                  className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center text-white font-bold text-lg ring-2 ring-white"
                  style={{ zIndex: coParents.length - i }}
                >
                  {(cp.partner_name || cp.partner_email || '?')[0].toUpperCase()}
                </div>
              ))}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 font-medium">
                {coParents.length === 1 ? 'Co-Parent' : 'Co-Parents'}
              </p>
              <p className="text-base font-semibold text-slate-800 truncate">
                {coParents.length === 1
                  ? (coParents[0].partner_name || coParents[0].partner_email || 'Linked account')
                  : `${coParents.length} accounts linked`}
              </p>
            </div>
            <span className="flex items-center gap-1 text-sm font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full flex-shrink-0">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              Linked
            </span>
          </div>
        </button>

        {/* Add another co-parent */}
        <button
          onClick={() => onNavigate('/coparent?invite=1')}
          className="w-full card px-4 py-3 flex items-center gap-3 text-left border border-dashed border-brand-500 active:scale-[0.98] transition-transform"
        >
          <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center text-brand-700 flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <p className="flex-1 text-sm font-semibold text-brand-700">Add a Co-Parent</p>
          <svg className="w-4 h-4 text-brand-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => onNavigate('/coparent')}
      className="w-full card p-4 flex items-center gap-3 text-left border-2 border-dashed border-slate-200 active:scale-[0.98] transition-transform"
    >
      <div className="w-11 h-11 rounded-xl bg-slate-200 flex items-center justify-center text-slate-600 flex-shrink-0">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      </div>
      <div className="flex-1">
        <p className="text-base font-semibold text-slate-700">Connect Co-Parent</p>
        <p className="text-sm text-slate-700">Share calendar & child info</p>
      </div>
      <svg className="w-5 h-5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  )
}

// ── Dismissable tip card (tap × or swipe to dismiss) ─────────────────────────
function DismissableTip({ tip }) {
  const [dismissed, setDismissed] = useState(false)
  const [exiting,   setExiting]   = useState(false)
  const [swipeX,    setSwipeX]    = useState(0)        // live drag offset px
  const touchStartX = useRef(null)
  const SWIPE_THRESHOLD = 72 // px needed to trigger dismiss

  const dismiss = useCallback(() => {
    if (exiting) return
    setExiting(true)
    // Let the exit animation play before removing from DOM
    setTimeout(() => setDismissed(true), 280)
  }, [exiting])

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const onTouchMove = (e) => {
    if (touchStartX.current === null) return
    const delta = e.touches[0].clientX - touchStartX.current
    setSwipeX(delta)
  }

  const onTouchEnd = () => {
    if (Math.abs(swipeX) >= SWIPE_THRESHOLD) {
      dismiss()
    } else {
      // Snap back
      setSwipeX(0)
    }
    touchStartX.current = null
  }

  if (dismissed) return null

  // While swiping: follow finger. While exiting: fly off in the swipe direction.
  const flyDir = swipeX >= 0 ? 1 : -1
  const translateX = exiting ? flyDir * 320 : swipeX
  const opacity    = exiting ? 0 : Math.max(0, 1 - Math.abs(swipeX) / 220)

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        transform:  `translateX(${translateX}px)`,
        opacity,
        transition: exiting || swipeX === 0
          ? 'transform 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.28s ease'
          : 'none',
      }}
      className="card bg-amber-50 border border-amber-200 flex gap-3 items-start animate-slide-up select-none"
    >
      <span className="text-2xl flex-shrink-0 mt-0.5">{tip.icon}</span>
      <p className="text-sm text-amber-800 leading-relaxed flex-1">{tip.text}</p>
      <button
        onClick={dismiss}
        aria-label="Dismiss tip"
        className="flex-shrink-0 -mt-0.5 -mr-0.5 w-7 h-7 flex items-center justify-center rounded-full text-amber-400 hover:text-amber-700 hover:bg-amber-200 transition-colors tap-target"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

// ── Dashboard screen ──────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate()
  const { user, setUser, logout, coParents = [], calendarEvents, deleteCalendarEvent,
          unreadCount, markNotifRead, messageCount,
          detectedLocation, setDetectedLocation, directMsgCount,
          comms, events, children: kidProfiles } = useApp()
  const [seen, markSeen] = useSeenFeatures()
  const [detailEvent, setDetailEvent] = useState(null)

  // ── Location detection ──────────────────────────────────────────────────────
  const { loc, showPrompt, detecting, locError, requestGps, dismiss } = useLocationDetect({
    onDetected(location) {
      setDetectedLocation(location)
      // Auto-fill user's state if not already set
      if (!user?.state && location.state) {
        setUser(prev => ({ ...prev, state: location.state }))
      }
    },
  })

  // Effective state for display: prefer GPS state > user profile state
  const effectiveState  = loc?.state  || user?.state  || null
  const effectiveCounty = loc?.county || null

  // ── Grouped modules: new/unseen float to top within their category ────────
  const groupedModules = useMemo(() => {
    return CATEGORIES.map(cat => ({
      ...cat,
      modules: modules.filter(m => m.category === cat.id),
    }))
  }, [])

  // AI modules (with header) + all other modules flattened and sorted so
  // any unseen "new" item always floats to the top of the grid.
  const isNewUnseen  = useCallback((mod) => !!mod.newSince && !seen[mod.id], [seen])
  const aiGroup      = groupedModules.find(c => c.id === 'ai')
  const otherModules = useMemo(() =>
    groupedModules
      .filter(c => c.id !== 'ai')
      .flatMap(c => c.modules)
      .sort((a, b) => (isNewUnseen(a) ? 0 : 1) - (isNewUnseen(b) ? 0 : 1)),
    [groupedModules, isNewUnseen]
  )

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  // Pick a random co-parenting affirmation once per session (on mount)
  const [affirmationIdx] = useState(() => Math.floor(Math.random() * COPARENTING_AFFIRMATIONS.length))

  const tip = user?.situation
    ? (user.situation === 'coparenting'
        ? COPARENTING_AFFIRMATIONS[affirmationIdx]
        : tips[user.situation])
    : null

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden no-scrollbar">
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="brand-gradient px-5 pb-6" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))' }}>
        <div className="relative flex items-start justify-between">
          <div>
            <p className="text-blue-200 text-xs font-medium tracking-wide uppercase">{greeting()}</p>
            <h1 className="text-white text-3xl font-extrabold mt-0.5 leading-tight">
              {user?.name || 'Parent'}
            </h1>
          </div>

          {/* ── Centered GoodGuardian court seal (Tribunal) ── */}
          <div
            className="absolute left-1/2 top-0 -translate-x-1/2 pointer-events-none select-none"
            role="img"
            aria-label="GoodGuardian court seal"
          >
            <div className="seal" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <IconScale size={20} stroke={1.75} className="text-gold-400 relative" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
            {/* Messages bell — navigates to secure direct messages */}
            <button
              onClick={() => navigate('/direct-messages')}
              className="relative tap-target text-blue-100 hover:text-white transition-colors"
              title="Secure Messages"
              aria-label="Open secure messages"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {directMsgCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[9px] font-bold flex items-center justify-center text-white animate-pulse">
                  {directMsgCount > 9 ? '9+' : directMsgCount}
                </span>
              )}
            </button>
            {/* Settings */}
            <button
              onClick={() => navigate('/settings')}
              className="tap-target text-blue-100 hover:text-white transition-colors"
              title="Settings"
              aria-label="Open settings"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={logout}
              className="tap-target text-blue-100 hover:text-white transition-colors"
              title="Sign out"
              aria-label="Sign out"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
            </div>
            {effectiveState && (
              <p className="text-blue-200/80 text-xs flex items-center gap-1">
                <span>📍</span>
                <span>{effectiveCounty ? `${effectiveCounty}, ` : ''}{effectiveState}</span>
                {loc?.source === 'gps' && <span className="opacity-60">· GPS</span>}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 -mt-5 pb-6 lg:pb-8">
        <div className="flex flex-col gap-5 lg:grid lg:grid-cols-12 lg:gap-6">
          <div className="flex flex-col gap-5 lg:col-span-8">
            {/* ── Tip card ───────────────────────────────────────────────────── */}
            {tip && <DismissableTip tip={tip} />}

            {/* ── Location permission prompt ──────────────────────────────────── */}
            {showPrompt && (
              <div className="card bg-blue-50 border border-blue-200 flex gap-3 items-start animate-slide-up">
                <span className="text-2xl flex-shrink-0">📍</span>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-blue-900">Enable location for precise family law guidance</p>
                  <p className="text-sm text-blue-700 mt-1 leading-relaxed">
                    GoodGuardian can automatically identify your state and county laws when you allow location access.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={requestGps}
                      className="px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-semibold active:scale-[0.97] transition-transform"
                    >
                      Allow Location
                    </button>
                    <button
                      onClick={dismiss}
                      className="px-4 py-2 rounded-lg bg-white border border-blue-200 text-blue-700 text-sm font-medium active:scale-[0.97] transition-transform"
                    >
                      Not Now
                    </button>
                  </div>
                </div>
                <button onClick={dismiss} className="text-blue-400 hover:text-blue-600 flex-shrink-0 text-xl leading-none tap-target">×</button>
              </div>
            )}

            {/* Location detecting spinner */}
            {detecting && (
              <div className="card bg-blue-50 border border-blue-200 flex items-center gap-3">
                <span className="text-xl">🔍</span>
                <p className="text-base text-blue-700 font-medium">Detecting your location…</p>
              </div>
            )}

            {/* Location error */}
            {locError && (
              <div className="card bg-amber-50 border border-amber-200 flex items-start gap-3">
                <span className="text-xl flex-shrink-0">⚠️</span>
                <p className="text-sm text-amber-800 leading-relaxed">{locError}</p>
              </div>
            )}

            {/* ── Modules grid ───────────────────────────────────────────────── */}
            <div className="flex flex-col gap-6">
              {/* AI Assistants */}
              <section>
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                  {aiGroup.modules.map(mod => (
                    <ModuleCard
                      key={mod.id}
                      mod={mod}
                      isNew={!!mod.newSince && !seen[mod.id]}
                      onSeen={markSeen}
                      onClick={navigate}
                      unreadCount={mod.id === 'direct-messages' ? directMsgCount : 0}
                    />
                  ))}
                </div>
              </section>

              {/* Upcoming events — between AI and other tools */}
              <UpcomingEventsCard
                calendarEvents={calendarEvents}
                onNavigate={navigate}
                onEventTap={setDetailEvent}
              />

              {/* All other tools — flat grid, no section header */}
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                {otherModules.map(mod => (
                  <ModuleCard
                    key={mod.id}
                    mod={mod}
                    isNew={!!mod.newSince && !seen[mod.id]}
                    onSeen={markSeen}
                    onClick={navigate}
                    unreadCount={mod.id === 'direct-messages' ? directMsgCount : 0}
                  />
                ))}
              </div>
            </div>

            {/* ── Quick actions — unique shortcuts not in nav ─────────────────── */}
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map(qa => (
                <button
                  key={qa.label}
                  onClick={async () => {
                    if (qa.to === '__court_report__') {
                      let expenses = []
                      try { const d = await dataApi.getExpenses?.(); expenses = d?.expenses || [] } catch {}
                      printCourtReport({
                        userName: user?.name, state: user?.state,
                        comms, events, calendarEvents, expenses,
                        children: kidProfiles, currentUserId: user?.id,
                      })
                    } else {
                      navigate(qa.to)
                    }
                  }}
                  className="flex items-center justify-center gap-3 py-4 px-5 rounded-2xl bg-brand-50 hover:bg-brand-100 active:scale-[0.97] transition-all tap-target"
                >
                  <span className="text-2xl leading-none">{qa.emoji}</span>
                  <span className="text-sm text-brand-700 font-semibold">{qa.label}</span>
                </button>
              ))}
            </div>

            {/* ── Legal disclaimer ───────────────────────────────────────────── */}
            <div className="disclaimer">
              ⚠️ AI-generated content is legal education, not legal advice. Always consult a
              licensed attorney before filing documents or making legal decisions.
            </div>
          </div>

          <div className="flex flex-col gap-5 lg:col-span-4 lg:pt-0.5">
            {/* ── Credit score widget ────────────────────────────────────────── */}
            <CreditScoreWidget onNavigate={navigate} />

            {/* ── Co-parent status ───────────────────────────────────────────── */}
            <CoParentStatusCard coParents={coParents} onNavigate={navigate} />

          </div>
        </div>
      </div>

      {/* ── Event detail bottom sheet ──────────────────────────────────────── */}
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
          onDelete={(id) => { deleteCalendarEvent(id); setDetailEvent(null) }}
          userId={user?.id}
          children={kidProfiles}
        />
      )}
    </div>
  )
}
